import { useCallback, useEffect, useRef, useState } from 'react';
import { apiErrorText } from 'Utils/errorText';
import { updateDataGraph, graphOutcome, withObject, withoutObject } from './dataGraphApi';

// A data graph page's ONE draft (2026-11-04): the settings panel, the object list's ticks, the rules
// editor and the extra starts all edit this form, and one Save writes it. Two editors on the same
// field with two saves is how a tick saved here was lost to a settings save there.

export const formOf = (g) => ({
  name: (g && g.name) || '',
  description: (g && g.description) || '',
  keyName: (g && g.keyName) || '',
  keyTable: (g && g.keyTable) || '',
  keyColumn: (g && g.keyColumn) || '',
  maxHops: g && g.maxHops != null ? String(g.maxHops) : '',
  outcome: graphOutcome(g),
  objects: ((g && g.objects) || []).map((o) => ({ ...o, rules: (o.rules || []).map((r) => ({ ...r })) })),
  starts: ((g && g.starts) || []).map((s) => ({ table: s.table || '', column: s.column || '' })),
});

/** The PUT body the form stands for. `hopLimited`: the source keeps a hop limit (relational). */
export const bodyOf = (graph, f, hopLimited) => ({
  dataSourceId: graph.dataSourceId,
  name: f.name.trim(),
  description: f.description.trim() || null,
  keyName: f.keyName.trim() || null,
  keyTable: f.keyTable.trim() || null,
  keyColumn: f.keyColumn.trim() || null,
  // kept while the graph is a list (the planner ignores it there), so flipping back loses nothing
  maxHops: hopLimited && /^\d+$/.test(f.maxHops.trim()) ? Number(f.maxHops.trim()) : null,
  outcome: f.outcome,
  objects: f.objects.map((o) => ({
    name: o.name.trim(),
    save: o.save !== false,
    rules: (o.rules || []).map((r) => ({
      column: String(r.column || '').trim(), source: r.source, sourceColumn: String(r.sourceColumn || '').trim(),
      maxPerParent: r.maxPerParent != null && r.maxPerParent !== '' ? Number(r.maxPerParent) : null,
      origin: r.origin === 'DERIVED' ? 'DERIVED' : 'GIVEN',
    })),
  })),
  starts: f.starts.map((s) => ({ table: String(s.table || '').trim(), column: String(s.column || '').trim() })),
});

/**
 * `graph` may still be loading (null). A row filter saved elsewhere on the page also replaces `graph`:
 * the form follows it, but never over edits the consultant is still making here. `onSaved(saved)` gets the
 * saved graph; a string it returns is the Save's notice.
 */
export default function useGraphDraft(graph, { hopLimited = false, onSaved } = {}) {
  const [form, setForm] = useState(() => formOf(graph));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const shown = useRef(formOf(graph));
  useEffect(() => {
    // Compare against what was shown BEFORE this graph arrived: React may run the updater lazily, after
    // `shown` has moved on (the page's own state updates batch with it), and the form would never follow.
    const next = formOf(graph);
    const before = shown.current;
    shown.current = next;
    setForm((f) => (JSON.stringify(f) === JSON.stringify(before) ? next : f));
  }, [graph]);

  const dirty = JSON.stringify(form) !== JSON.stringify(formOf(graph));
  // React 16 pools the event: read the value now, not inside the updater.
  const set = useCallback((k) => (e) => { const { value } = e.target; setForm((f) => ({ ...f, [k]: value })); setNotice(null); }, []);
  const setObjects = useCallback((objects) => { setForm((f) => ({ ...f, objects: typeof objects === 'function' ? objects(f.objects) : objects })); setNotice(null); }, []);
  const setStarts = useCallback((starts) => { setForm((f) => ({ ...f, starts })); setNotice(null); }, []);
  const reset = useCallback(() => { setForm(formOf(graph)); setNotice(null); }, [graph]);
  const body = graph ? bodyOf(graph, form, hopLimited) : null;

  const save = useCallback(async () => {
    if (!graph) return null;
    setBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, bodyOf(graph, form, hopLimited));
      // `onSaved` may answer with the notice to show instead (the page: "Saved. Testing it…").
      const said = onSaved ? onSaved(saved) : null;
      setNotice({ tone: 'ok', text: typeof said === 'string' && said ? said : 'Saved. Subsets that use this data graph pick it up on their next preview or run.' });
      return saved;
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not save the data graph') });
      return null;
    } finally {
      setBusy(false);
    }
  }, [graph, form, hopLimited, onSaved]);

  return { form, setForm, set, setObjects, setStarts, dirty, reset, save, busy, notice, setNotice, body };
}

/**
 * The draft's object list as the object views' "choice" (the same shape useObjectChoice gives, kind
 * `graph`): tick = list the object (saved, no rule yet: the Test derives one), untick = drop it and the
 * rules that read it. Saving is the page's one Save.
 */
export const graphChoice = (draft, graph) => {
  const objects = (draft.form && draft.form.objects) || [];
  const picked = new Set(objects.map((o) => o.name));
  const saved = new Set(((graph && graph.objects) || []).map((o) => o.name));
  return {
    kind: 'graph',
    picked,
    saved,
    dirty: draft.dirty,
    saving: draft.busy,
    saveError: draft.notice && draft.notice.tone === 'error' ? draft.notice.text : null,
    toggle: (table) => draft.setObjects((list) => (list.some((o) => String(o.name).toLowerCase() === String(table).toLowerCase())
      ? withoutObject(list, table) : withObject(list, table))),
    pickAll: (tables, on) => draft.setObjects((list) => tables.reduce((acc, t) => (on ? withObject(acc, t) : withoutObject(acc, t)), list)),
    save: draft.save,
    revert: draft.reset,
    clear: () => draft.setObjects([]),
  };
};
